<script setup lang="ts">
import type {VariantProps} from 'class-variance-authority'
import {cva} from 'class-variance-authority'

const style = cva(
  [
    'font-medium bg-gradient-to-b flex items-center justify-center',
    'transition-all duration-300 hover:duration-100 transform active:translate-y-0.5',
  ],
  {
    variants: {
      intent: {
        main: 'from-main-500 to-main-550 text-white bordershadow-main-highlight hover:bordershadow-lg-main ',
        normal: 'from-gray-50 to-[#F6F6F6] text-gray-900 bordershadow-light-highlight hover:bordershadow-medium',
      },
      size: {
        sm: 'text-sm px-3 py-1.5 rounded',
        normal: 'text-base px-3 py-1.5 rounded-md',
        lg: 'text-base px-6 py-3 rounded-lg',
      },
    },
  }
)
type StyleProps = VariantProps<typeof style>

const props = withDefaults(
  defineProps<{
    as?: 'button' | 'link'
    to?: string
    intent?: StyleProps['intent']
    size?: StyleProps['size']
    /** Filename for download attribute. When set, the link triggers a file download. */
    download?: string
  }>(),
  {
    as: 'button',
    intent: 'normal',
    size: 'normal',
  }
)

const isExternal = computed(() => props.to?.includes('://') ?? false)

const componentType = computed(() => {
  if (props.as !== 'link') return 'button'
  if (isExternal.value || props.download) return 'a'
  return resolveComponent('NuxtLink') as string
})

const linkAttrs = computed(() => {
  if (props.as !== 'link') return {}

  const attrs: Record<string, string | undefined> = {}

  if (isExternal.value) {
    attrs.target = '_blank'
    attrs.rel = 'noopener noreferrer'
  }

  if (props.download) {
    attrs.download = props.download
  }

  return attrs
})
</script>

<template>
  <component
    :is="componentType"
    :class="style({intent, size, class: ($attrs.class as string) ?? undefined})"
    :href="to"
    v-bind="linkAttrs"
  >
    <slot />
  </component>
</template>
